import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { ArrowRight, ChevronLeft, ChevronRight, Megaphone } from 'lucide-react';

// Homepage offers carousel — auto-rotates every 6s, respects hover pause,
// arrow + dot navigation. Reads from the public `/banners/live` endpoint so
// admins can push any offer instantly via /admin → Ops Queue → Banners.
const GRADIENTS = {
  yellow: 'from-[#FCD535] to-[#F0B90B]',
  green:  'from-[#0ECB81] to-[#0AB673]',
  red:    'from-[#F6465D] to-[#E33A50]',
  violet: 'from-[#8B5CF6] to-[#6D28D9]',
  blue:   'from-[#4285F4] to-[#1E60D0]',
};

export default function OffersBanner() {
  const [banners, setBanners] = useState([]);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const seenRef = useRef(new Set()); // session-local dedupe key

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/banners/live');
        if (alive) setBanners(r.data.banners || []);
      } catch (_e) { if (alive) setBanners([]); }
    })();
    return () => { alive = false; };
  }, []);

  // Fire an impression exactly once per banner per session (avoid inflating on rotate)
  useEffect(() => {
    if (!banners.length) return;
    const b = banners[i];
    if (!b?.banner_id) return;
    if (seenRef.current.has(b.banner_id)) return;
    seenRef.current.add(b.banner_id);
    api.post(`/banners/${b.banner_id}/impression`).catch(() => {});
  }, [i, banners]);

  const trackClick = (b) => {
    api.post(`/banners/${b.banner_id}/click`).catch(() => {});
  };

  useEffect(() => {
    if (banners.length <= 1 || paused) return;
    timer.current = setInterval(() => setI(v => (v + 1) % banners.length), 6000);
    return () => clearInterval(timer.current);
  }, [banners.length, paused]);

  if (banners.length === 0) return null;
  const b = banners[i];
  const grad = GRADIENTS[b.gradient] || GRADIENTS.yellow;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-4" data-testid="offers-banner">
      <div
        className={`relative overflow-hidden bg-gradient-to-r ${grad} rounded-sm border border-white/10`}
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      >
        <div className="px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="hidden md:grid w-12 h-12 rounded-full bg-black/20 place-items-center flex-shrink-0">
            <Megaphone className="w-6 h-6 text-black/80" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {b.badge && (
                <span className="text-[10px] uppercase font-mono tracking-wider bg-black/25 text-white px-2 py-0.5 rounded-sm" data-testid="offers-badge">
                  {b.badge}
                </span>
              )}
              <div className="text-black font-bold text-lg md:text-xl leading-tight" data-testid="offers-title">{b.title}</div>
            </div>
            {b.subtitle && (
              <div className="text-black/70 text-xs md:text-sm mt-1" data-testid="offers-subtitle">{b.subtitle}</div>
            )}
          </div>
          <a href={b.cta_url || '/'}
             onClick={() => trackClick(b)}
             className="bg-[#0B0E11] hover:bg-[#181A20] text-white font-semibold text-xs md:text-sm uppercase px-4 py-2.5 rounded-sm inline-flex items-center gap-1.5 flex-shrink-0 transition-colors"
             data-testid="offers-cta">
            {b.cta_label || 'Learn more'} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>

        {banners.length > 1 && (
          <>
            <button onClick={() => setI(v => (v - 1 + banners.length) % banners.length)}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/25 hover:bg-black/40 grid place-items-center"
              aria-label="Previous offer" data-testid="offers-prev">
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <button onClick={() => setI(v => (v + 1) % banners.length)}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/25 hover:bg-black/40 grid place-items-center"
              aria-label="Next offer" data-testid="offers-next">
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1" data-testid="offers-dots">
              {banners.map((_, idx) => (
                <button key={idx} onClick={() => setI(idx)}
                  className={`w-1.5 h-1.5 rounded-full ${idx === i ? 'bg-black' : 'bg-black/30'} transition-colors`}
                  aria-label={`Go to offer ${idx + 1}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
